import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface Category { id: string; name: string }

interface Props {
  onClose: () => void;
}

export function AddAchievementModal({ onClose }: Props) {
  const qc = useQueryClient();
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/api/categories') });

  const [type, setType] = useState<'due_date' | 'recurring'>('recurring');
  const [form, setForm] = useState({ name: '', categoryId: '', freqCount: 1, freqPeriodHours: 24, dueAt: '' });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = useMutation({
    mutationFn: () => api('/api/achievements', {
      method: 'POST',
      body: JSON.stringify(type === 'recurring'
        ? { name: form.name, categoryId: form.categoryId, type,
            freqCount: form.freqCount, freqPeriodHours: form.freqPeriodHours }
        : { name: form.name, categoryId: form.categoryId, type,
            dueAt: new Date(form.dueAt).toISOString() }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['achievements'] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'hata'),
  });

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); setErr(null); create.mutate(); }}
        onClick={(e) => e.stopPropagation()}
        className="w-96 space-y-3 rounded-xl border border-border-soft bg-bg-raised p-6 shadow-2xl"
      >
        <h3 className="text-lg font-semibold text-neutral-100">Yeni Achievement</h3>
        <div className="flex gap-2">
          <select value={type} onChange={(e) => setType(e.target.value as typeof type)}
                  className="rounded-md bg-bg-elevated px-3 py-2 text-neutral-100">
            <option value="recurring">Rutin</option>
            <option value="due_date">Proje</option>
          </select>
          <input placeholder="İsim" value={form.name} required autoFocus
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="flex-1 rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        </div>
        <select value={form.categoryId} required
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100">
          <option value="">Kategori seç…</option>
          {(cats.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {type === 'recurring' ? (
          <div className="flex items-center gap-2">
            <input type="number" min={1} value={form.freqCount}
                   onChange={(e) => setForm({ ...form, freqCount: Number(e.target.value) })}
                   className="w-20 rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
            <span className="text-neutral-400">/</span>
            <input type="number" min={1} value={form.freqPeriodHours}
                   onChange={(e) => setForm({ ...form, freqPeriodHours: Number(e.target.value) })}
                   className="w-20 rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
            <span className="text-sm text-neutral-400">saatte</span>
          </div>
        ) : (
          <input type="datetime-local" value={form.dueAt} required
                 onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
                 className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        )}
        {err && <p className="text-sm text-red-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-md px-3 py-2 text-sm text-neutral-400 hover:text-neutral-100">
            İptal
          </button>
          <button type="submit" disabled={create.isPending}
                  className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            Ekle
          </button>
        </div>
      </form>
    </div>
  );
}
